import type { Recipe } from '../types';

// The classic Mississippi pot roast, Instant Pot style: two packets, butter,
// pepperoncinis, dump and go. The only add is a bag of baby carrots under the
// roast (no peeling) that gets mashed into the juice. The clean, organic,
// from-scratch version is its own recipe: homemade-mississippi-pot-roast.

export const mississippiPotRoast: Recipe = {
  slug: 'mississippi-pot-roast',
  title: 'Mississippi Pot Roast',
  meal: 'dinner',
  category: 'meal',
  types: ['stew', 'sandwich'],
  cuisines: ['american'],
  traits: ['easy-prep', 'freezer-friendly', 'one-pot', 'hidden-veg', 'high-protein', 'make-ahead'],
  yield: '8 servings',
  activeTime: '10 min',
  totalTime: '1 hr 30 min',
  freezerLife: '3 months',
  difficulty: 1,

  summary:
    'The famous one: chuck roast, a ranch packet, an au jus packet, butter and pepperoncinis in the Instant Pot. Dump, seal, walk away. Baby carrots go under the roast and get mashed into the gravy, so there are vegetables in it and nobody can tell.',

  flavorNotes:
    'Salty, savory, buttery, with a tangy kick from the pepperoncini brine. That tang is the whole point; it is what keeps the butter from tasting heavy. Not spicy, because pepperoncinis are mild. The packets do the seasoning, so taste before you add any salt. You almost never need it.',

  ingredients: [
    {
      id: 'beef',
      name: 'Chuck roast (or arm / shoulder roast)',
      total: '3 lb',
      prep: 'thawed or frozen; see the step 1 tip for which cuts work',
      fromFreezer: true,
      uses: [{ step: 1, amount: '3 lb' }],
    },
    {
      id: 'water',
      name: 'Water',
      total: '1 cup',
      shop: false,
      uses: [{ step: 1, amount: '1 cup' }],
    },
    {
      id: 'baby-carrots',
      name: 'Baby carrots',
      total: '1 lb',
      prep: 'straight from the bag',
      uses: [{ step: 1, amount: '1 lb' }],
      shop: 'Marketside baby carrots, 1 lb bag',
    },
    {
      id: 'ranch-packet',
      name: 'Hidden Valley Original Ranch seasoning, 1 oz packet',
      total: '1 packet',
      uses: [{ step: 2, amount: '1 packet' }],
    },
    {
      id: 'au-jus-packet',
      name: 'McCormick Au Jus gravy mix, 1 oz packet',
      total: '1 packet',
      uses: [{ step: 2, amount: '1 packet' }],
    },
    {
      id: 'pepperoncini',
      name: 'Whole pepperoncini peppers, plus brine',
      total: '8 peppers + ¼ cup brine',
      uses: [{ step: 2, amount: '8 peppers + ¼ cup brine' }],
      shop: 'Great Value whole pepperoncini, 12 fl oz jar',
    },
    {
      id: 'butter',
      name: 'Butter',
      total: '4 tbsp',
      prep: 'half a stick, in pats',
      uses: [{ step: 2, amount: '4 tbsp' }],
      shop: 'Great Value unsalted butter, 1 lb',
    },
    {
      id: 'cornstarch',
      name: 'Cornstarch',
      total: '2 tbsp',
      optional: true,
      optionalKind: 'upgrade',
      optionalWhy: 'Turns the juice into gravy for potatoes. Skip it for sandwiches, because thin juice soaks the roll better.',
      uses: [{ step: 5, amount: '2 tbsp' }],
    },
    {
      id: 'potatoes',
      name: 'Russet potatoes, for mashing',
      total: '3 lb',
      optional: true,
      optionalKind: 'serve-with',
      optionalWhy: 'The classic plate. Hoagie rolls from this cookbook are the other move.',
      uses: [{ step: 5, amount: '3 lb' }],
      shop: 'Russet potatoes, 5 lb bag',
    },
  ],

  steps: [
    {
      n: 1,
      title: 'Load the pot',
      time: '3 min',
      critical: true,
      ingredientIds: ['beef', 'water', 'baby-carrots'],
      body:
        'Pour the {1 cup|water} of water into the Instant Pot. Dump in the {1 lb|baby-carrots} of baby carrots as a bed, then set the {3 lb|beef} roast on top. Thawed or frozen solid both work; the timing changes in step 3.',
      tip:
        'Cuts that work: chuck roast, chuck eye, arm roast, shoulder / clod roast, blade or 7-bone roast, cross-rib (English) roast, brisket point. Anything marbled with seams works. Pikes Peak (heel of round) works but runs leaner and stringier, so add 15 minutes. Bottom round, rump and sirloin tip are passable the same way. Do not use eye of round, top round, sirloin, tri-tip, rib roast or tenderloin. They dry out, will not shred, and the good ones are wasted here. Got 10 extra minutes? Searing the roast on Sauté first adds real flavor, but it is not required.',
    },
    {
      n: 2,
      title: 'Packets, peppers, butter',
      time: '2 min',
      ingredientIds: ['ranch-packet', 'au-jus-packet', 'pepperoncini', 'butter'],
      body:
        'Sprinkle the ranch packet and the au jus packet evenly over the top of the roast, on the meat and not the pot floor. Tuck the pepperoncinis around it, pour {¼ cup} of their brine over the top, and lay the {4 tablespoons|butter} of butter in pats across the roast. Do not stir.',
      tip:
        'Dry packet powder on the bottom of the pot is what triggers the Burn warning. Keep it on top of the meat.',
    },
    {
      n: 3,
      title: 'Pressure cook',
      time: '60 min + 15 min release',
      ingredientIds: [],
      body:
        'Lock the lid, valve to Sealing, Pressure Cook on High for 60 minutes (90 if the roast went in frozen). When it beeps, do a natural release: let the pressure drop on its own for 15 minutes, then turn the valve to Venting for the rest. If the pot ever flashes Burn, the bottom is scorching. Open it, scrape the bottom, add a splash of water, and restart.',
      tip:
        'Venting straight off high pressure makes the meat tighten up. The 15-minute wait is what keeps it tender.',
    },
    {
      n: 4,
      title: 'Shred and hide the carrots',
      time: '5 min',
      critical: true,
      ingredientIds: [],
      body:
        'Lift the roast onto a board and shred it with two forks, pulling out the big fat pieces. Mash the carrots right in the pot with a potato masher until they melt into the juice. Stir the meat back in.',
      tip:
        'A pound of carrots mashed into the juice just reads as a slightly sweeter, thicker gravy. Nobody finds a carrot.',
    },
    {
      n: 5,
      title: 'Thicken (optional) and serve',
      time: '3 min',
      ingredientIds: ['cornstarch', 'potatoes'],
      body:
        'For gravy over potatoes: set the pot to Sauté, stir the {2 tablespoons|cornstarch} of cornstarch into {3 tablespoons} of cold water, and whisk it into the bubbling juice for 1 to 2 minutes. For sandwiches, skip it. Serve over mashed potatoes, on hoagie rolls with provolone under the broiler, or over rice. Leftovers make good quesadillas.',
    },
  ],

  freezing:
    'Cool completely, then pack quart bags with plenty of juice in each. The juice keeps shredded beef from drying out on reheat. Freeze flat with the air pressed out, about 3 months. Gravy thickened with cornstarch comes back a little thinner and thickens again with a minute of simmering.',

  reheat: [
    {
      method: 'Stovetop',
      best: true,
      detail: 'Thaw overnight. Covered saucepan on medium-low with a splash of water, about 10 minutes. Keep it at a simmer, not a boil, or the beef goes stringy.',
    },
    {
      method: 'Microwave',
      detail: 'Thawed, covered, 70% power in 90-second rounds, stirring between. Make sure there is juice in the bowl.',
    },
    {
      method: 'Instant Pot from frozen',
      detail: 'Frozen block in with ½ cup water, Pressure Cook 10 minutes, quick release.',
    },
  ],

  glossary: [
    { term: 'natural release', plain: 'Letting the pot lose pressure on its own instead of flipping the valve. The meat relaxes as the pressure drops slowly, which keeps it tender.' },
    { term: 'Burn', plain: 'The Instant Pot warning that something is scorching on the bottom. It stops cooking. Usually caused by powder or stuck bits on the pot floor.' },
    { term: 'shred', plain: 'Pull cooked meat apart along its grain with two forks. If it fights you, it needs more time under pressure, not more force.' },
  ],

  inspiration: [
    { label: 'McCormick — Instant Pot Mississippi Roast', url: 'https://mccormick.com/recipes/main-dishes/instant-pot-mississippi-roast' },
    { label: 'Diethood — Instant Pot Mississippi Pot Roast', url: 'https://diethood.com/instant-pot-mississippi-pot-roast/' },
  ],

  whereWeVeered:
    'This is the classic dump-and-go with three changes. A measured cup of water goes in, because an Instant Pot needs liquid to come up to pressure. A bag of baby carrots goes under the roast and gets mashed into the gravy. And the butter is cut from a full stick to half, because the roast is plenty rich. For the clean, organic, no-packet version, see Homemade Mississippi Pot Roast.',
};
